"use client"
import { useState, useEffect, ReactNode } from 'react';
import { X, Play } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export type OwnerVideo = { id: string; title: string; label: string };

// Owner testimonial + Q&A videos supplied by HOPO Homes (YouTube).
export const OWNER_TESTIMONIAL_VIDEOS: OwnerVideo[] = [
    { id: "x-62Gy65_GU", title: "Best returns to our property owners", label: "Testimonial" },
    { id: "YA4XE2xvJa0", title: "Why owners trust HOPO Homes", label: "Testimonial" },
    { id: "tEYpa0PoYE8", title: "Hassle-free property management", label: "Testimonial" },
];

export const OWNER_QA_VIDEOS: OwnerVideo[] = [
    { id: "DoBrMLTpJbA", title: "Owner questions, answered", label: "Owner Q&A" },
    { id: "MZW4KuUuNRY", title: "How management works", label: "Owner Q&A" },
    { id: "zA_Fq27A-rg", title: "Maximising your ROI", label: "Owner Q&A" },
];

interface OwnerVideosProps {
    videos: OwnerVideo[];
    eyebrow?: string;
    heading: ReactNode;
    subtitle?: string;
}

const OwnerVideos = ({ videos, eyebrow = "Owner Testimonials", heading, subtitle }: OwnerVideosProps) => {
    const [selectedId, setSelectedId] = useState<string | null>(null);

    // Lock scroll when the lightbox is open
    useEffect(() => {
        document.body.style.overflow = selectedId ? 'hidden' : 'unset';
        return () => { document.body.style.overflow = 'unset'; };
    }, [selectedId]);

    return (
        <section className="bg-background py-16 md:py-24 overflow-hidden">
            <div className="container mx-auto px-5 md:px-10 max-w-7xl">
                {/* Header */}
                <div className="flex flex-col items-center text-center mb-12 md:mb-16">
                    <div className="flex items-center gap-2 mb-6">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                        <span className="text-[12px] font-semibold uppercase tracking-[1.5px] text-muted-foreground">
                            {eyebrow}
                        </span>
                    </div>
                    <h2 className="text-[32px] md:text-[44px] lg:text-[52px] font-medium leading-[1.15] text-foreground mb-6 tracking-[-0.02em]">
                        {heading}
                    </h2>
                    {subtitle && (
                        <p className="text-muted-foreground text-[15px] md:text-[16px] leading-[1.7] max-w-[600px]">
                            {subtitle}
                        </p>
                    )}
                </div>

                {/* Video grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {videos.map((video) => (
                        <button
                            key={video.id}
                            type="button"
                            onClick={() => setSelectedId(video.id)}
                            className="group relative aspect-video bg-neutral-900 rounded-[1.5rem] overflow-hidden shadow-xl text-left transition-all duration-500 hover:shadow-primary/10 hover:-translate-y-1"
                        >
                            {/* YouTube thumbnail */}
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                                src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
                                alt={video.title}
                                title={video.title}
                                loading="lazy"
                                className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-70 group-hover:opacity-50 transition-opacity duration-500" />

                            {/* Play button */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="w-16 h-16 rounded-full bg-white/15 backdrop-blur-md border border-white/30 flex items-center justify-center text-white group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                                    <Play size={26} className="ml-1" fill="currentColor" />
                                </span>
                            </div>

                            {/* Caption */}
                            <div className="absolute inset-x-0 bottom-0 p-5 z-10">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className="w-1 h-1 rounded-full bg-primary" />
                                    <span className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.2em] text-white/70">
                                        {video.label}
                                    </span>
                                </div>
                                <h3 className="text-lg md:text-xl font-medium text-white tracking-tight">
                                    {video.title}
                                </h3>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Lightbox */}
            <AnimatePresence>
                {selectedId && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-2xl p-4 md:p-10"
                        onClick={() => setSelectedId(null)}
                    >
                        <button
                            className="absolute top-6 right-6 md:top-10 md:right-10 w-12 h-12 md:w-16 md:h-16 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all z-[110]"
                            onClick={(e) => { e.stopPropagation(); setSelectedId(null); }}
                            aria-label="Close video"
                        >
                            <X size={24} />
                        </button>
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="relative w-full max-w-5xl aspect-video rounded-[1.5rem] md:rounded-[2rem] overflow-hidden shadow-[0_0_100px_rgba(0,0,0,0.5)] border border-white/10 bg-black"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <iframe
                                className="w-full h-full"
                                src={`https://www.youtube-nocookie.com/embed/${selectedId}?autoplay=1&rel=0`}
                                title="Owner testimonial"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default OwnerVideos;
